import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer } from "lucide-react";
import { useLogo } from "@/lib/useLogo";
import { trDates, formatDateTime } from "@/lib/constants";

const changesText = (c) => (c || []).map((x) => `${x.field}: ${x.old} → ${x.new}`).join("; ");

export default function VillageAuditPrintPage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const dateFrom = sp.get("date_from") || "";
  const dateTo = sp.get("date_to") || "";
  const fmt = (d) => d.split("-").reverse().join(".");
  const period = dateFrom || dateTo ? `Dönem: ${dateFrom ? fmt(dateFrom) : "başlangıç"} – ${dateTo ? fmt(dateTo) : "bugün"}` : "";
  const [village, setVillage] = useState(null);
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [union, setUnion] = useState({ name: "Köylere Hizmet Götürme Birliği", address: "", phone: "" });
  const { url: logoUrl } = useLogo();

  useEffect(() => {
    api.get(`/villages/${id}`).then((r) => { setVillage(r.data); document.title = `${r.data.name} İşlem Geçmişi`; })
      .catch((e) => setError(e?.response?.data?.detail || "Köy yüklenemedi"));
    const p = { page_size: 2000 };
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    api.get(`/villages/${id}/audit-logs`, { params: p }).then((r) => setItems(r.data.items))
      .catch(() => setItems([]));
    api.get("/settings/union").then((r) => setUnion(r.data)).catch(() => {});
  }, [id, dateFrom, dateTo]);

  if (error) return <div className="p-10 text-slate-600" data-testid="audit-print-error">{error}</div>;
  if (!village || !items) return <div className="p-10 text-slate-500">Yükleniyor...</div>;
  const contact = [union.address, union.phone ? `Tel: ${union.phone}` : ""].filter(Boolean).join(" · ");

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white py-8 print:py-0">
      <style>{"@page { size: A4 landscape; margin: 10mm; } @media print { .no-print { display: none !important; } }"}</style>
      <div className="no-print max-w-[297mm] mx-auto mb-3 flex justify-end px-2">
        <button onClick={() => window.print()} data-testid="audit-print-now-button"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium transition-colors">
          <Printer className="h-4 w-4" /> Yazdır / PDF olarak kaydet
        </button>
      </div>
      <article className="bg-white max-w-[297mm] mx-auto p-[10mm] print:p-0 shadow-lg print:shadow-none text-slate-900 overflow-x-auto"
               data-testid="audit-print-sheet">
        <header className="flex items-end justify-between gap-4 border-b-2 border-slate-900 pb-2">
          <div className="flex items-center gap-3 min-w-0">
            {logoUrl && <img src={logoUrl} alt="Birlik logosu" className="h-12 w-auto max-w-[40mm] object-contain" />}
            <div className="min-w-0">
              <div className="text-[11px] font-semibold text-slate-600">{union.name}</div>
              <h1 className="text-[18px] font-bold leading-tight">{village.name} Köyü İşlem Geçmişi</h1>
              {contact && <div className="text-[9.5px] text-slate-500 mt-0.5">{contact}</div>}
            </div>
          </div>
          <div className="text-[10px] text-slate-600 text-right">{period && <span data-testid="audit-print-period">{period}<br /></span>}Toplam {items.length} işlem<br />{new Date().toLocaleDateString("tr-TR")}</div>
        </header>
        {items.length === 0 ? (
          <div className="text-[11px] text-slate-500 mt-4" data-testid="audit-print-empty">Bu köyün kayıtlarında henüz işlem yapılmadı.</div>
        ) : (
          <table className="w-full text-[9.5px] leading-tight border-collapse mt-3 min-w-[700px]" data-testid="audit-print-table">
            <thead>
              <tr>{["Tarih / Saat", "Kullanıcı", "İşlem", "Kayıt Türü", "Kayıt", "Değişiklikler"].map((h) => (
                <th key={h} className="text-left font-semibold text-slate-700 border-b border-slate-300 py-1 pr-2">{h}</th>))}</tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="align-top break-inside-avoid">
                  <td className="py-1 pr-2 border-b border-slate-100 whitespace-nowrap">{formatDateTime(a.at)}</td>
                  <td className="py-1 pr-2 border-b border-slate-100 whitespace-nowrap">{a.user_name}</td>
                  <td className="py-1 pr-2 border-b border-slate-100">{a.action}</td>
                  <td className="py-1 pr-2 border-b border-slate-100 whitespace-nowrap">{a.entity}</td>
                  <td className="py-1 pr-2 border-b border-slate-100">{trDates(a.label) || "-"}</td>
                  <td className="py-1 border-b border-slate-100">{changesText(a.changes) || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="mt-5 pt-1.5 border-t border-slate-200 text-[9px] text-slate-500 text-center">
          {[union.name, contact].filter(Boolean).join(" · ")}
        </div>
      </article>
    </div>
  );
}
